import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ReactNode, useCallback, useState } from 'react';
import { FormSection } from './FormSection';
import Button from './core/Button';
import { IconTrash } from '@/icons';
import { FormElement } from './FormElement';
import { InputCheckBox } from './Inputs/InputCheckBox';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';

const deleteConfirmationSchema = z.object({
  confirmed: z.boolean(),
});

type DeleteConfirmationType = z.infer<typeof deleteConfirmationSchema>;

interface DeleteConfirmationFormProps {
  title: string;
  warningMessage: ReactNode;
  canDelete: boolean;
  onDelete: () => Promise<void> | void;
}

export const DeleteConfirmationForm = ({
  title,
  warningMessage,
  canDelete,
  onDelete,
}: DeleteConfirmationFormProps) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<DeleteConfirmationType>({
    resolver: zodResolver(deleteConfirmationSchema, {
      errorMap: useCustomErrorMap(),
    }),
    defaultValues: {
      confirmed: confirmed,
    },
    mode: 'onBlur',
  });

  const startEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const cancelEdit = useCallback(() => {
    methods.setValue('confirmed', false, {
      shouldDirty: true,
    });
    setConfirmed(false);
    setEditMode(false);
  }, [methods]);

  const onSubmit: SubmitHandler<DeleteConfirmationType> =
    useCallback(async () => {
      setIsLoading(true);
      await onDelete();
      methods.setValue('confirmed', false, {
        shouldDirty: true,
      });
      setConfirmed(false);
      setEditMode(false);
      setIsLoading(false);
    }, [onDelete, methods]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FormSection
          title={title}
          buttonType="lockButton"
          onClickButton={startEdit}
          editMode={editMode}
          canSeeButton={canDelete}
        >
          <div className="mb-2 flex justify-start">
            <span className="text-base font-normal">{warningMessage}</span>
          </div>
          {editMode && (
            <FormElement
              label={
                <FormattedMessage defaultMessage="Jag har förstått villkoren" />
              }
              input={
                <InputCheckBox
                  label=""
                  name="confirmed"
                  control={methods.control}
                  editMode={editMode}
                  onChange={() => {
                    const isConfirmed = !methods.getValues('confirmed');
                    methods.setValue('confirmed', isConfirmed, {
                      shouldDirty: true,
                    });
                    setConfirmed(isConfirmed);
                  }}
                />
              }
            />
          )}
          {!editMode && canDelete && (
            <div className="flex justify-start">
              <span className="text-base font-normal italic">
                <FormattedMessage defaultMessage="Lås upp för att fortsätta." />
              </span>
            </div>
          )}
          {editMode && (
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={cancelEdit}>
                <FormattedMessage defaultMessage="Avbryt" />
              </Button>
              <Button
                color="red"
                type="submit"
                variant="filled"
                startIcon={<IconTrash />}
                disabled={!methods.getValues('confirmed')}
                loading={isLoading}
              >
                <FormattedMessage defaultMessage="Radera" />
              </Button>
            </div>
          )}
        </FormSection>
      </form>
    </FormProvider>
  );
};
